import { EventBus } from '../core/event-bus';
import { Logger } from '../core/logger';
import { ReconnectionStrategy } from './reconnection-strategy';
import { MessageQueue } from './message-queue';
import type { OutgoingMessage, IncomingMessage, HandshakeMessage, PingMessage } from '../types/messages';

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private url: string;
  private apiKey: string;
  private sessionId: string;
  private eventBus: EventBus;
  private logger: Logger;
  private reconnectionStrategy: ReconnectionStrategy;
  private messageQueue: MessageQueue;
  private pingInterval: number | null = null;
  private reconnectEnabled: boolean;
  private reconnectTimeout: number | null = null;

  constructor(
    url: string,
    apiKey: string,
    sessionId: string,
    eventBus: EventBus,
    logger: Logger,
    reconnectEnabled = true
  ) {
    this.url = url;
    this.apiKey = apiKey;
    this.sessionId = sessionId;
    this.eventBus = eventBus;
    this.logger = logger;
    this.reconnectEnabled = reconnectEnabled;
    this.reconnectionStrategy = new ReconnectionStrategy(logger);
    this.messageQueue = new MessageQueue(100);
  }

  async connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        this.logger.info(`Connecting to ${this.url}...`);
        this.ws = new WebSocket(this.url);

        this.ws.onopen = () => {
          this.logger.info('WebSocket connected');
          this.reconnectionStrategy.reset();
          this.sendHandshake();
          this.startHeartbeat();
          this.flushQueue();
          this.eventBus.emit('ws:connected');
          resolve();
        };

        this.ws.onmessage = (event) => {
          this.handleMessage(event.data);
        };

        this.ws.onerror = (error) => {
          this.logger.error('WebSocket error', error);
          this.eventBus.emit('ws:error', error);
          reject(error);
        };

        this.ws.onclose = () => {
          this.logger.warn('WebSocket disconnected');
          this.stopHeartbeat();
          this.eventBus.emit('ws:disconnected');
          if (this.reconnectEnabled) {
            this.scheduleReconnect();
          }
        };
      } catch (error) {
        this.logger.error('Failed to connect', error);
        reject(error);
      }
    });
  }

  disconnect(): void {
    this.reconnectEnabled = false;
    this.stopHeartbeat();
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  send(message: OutgoingMessage): void {
    if (this.isConnected()) {
      const payload = JSON.stringify(message);
      this.ws!.send(payload);
      this.logger.debug('Message sent', message.type);
    } else {
      this.logger.warn('Not connected, queuing message', message.type);
      this.messageQueue.enqueue(message);
    }
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  private sendHandshake(): void {
    const handshake: HandshakeMessage = {
      type: 'handshake',
      timestamp: Date.now(),
      payload: {
        apiKey: this.apiKey,
        sessionId: this.sessionId,
        sdkVersion: '0.1.0'
      }
    };
    this.send(handshake);
  }

  private handleMessage(data: string): void {
    try {
      const message: IncomingMessage = JSON.parse(data);
      this.logger.debug('Message received', message.type);

      if (message.type === 'pong') {
        // Heartbeat response
        return;
      }

      this.eventBus.emit('ws:message', message);
    } catch (error) {
      this.logger.error('Failed to parse message', error);
    }
  }

  private startHeartbeat(): void {
    this.pingInterval = window.setInterval(() => {
      if (this.isConnected()) {
        const ping: PingMessage = {
          type: 'ping',
          timestamp: Date.now()
        };
        this.send(ping);
      }
    }, 30000); // 30 seconds
  }

  private stopHeartbeat(): void {
    if (this.pingInterval !== null) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private async scheduleReconnect(): Promise<void> {
    const delay = this.reconnectionStrategy.getNextDelay();
    if (delay === null) {
      this.logger.error('Max reconnection attempts reached');
      this.eventBus.emit('ws:max-retries');
      return;
    }

    this.logger.info(`Reconnecting in ${delay}ms...`);
    this.eventBus.emit('ws:reconnecting', { delay });

    this.reconnectTimeout = window.setTimeout(async () => {
      try {
        await this.connect();
      } catch (error) {
        this.logger.error('Reconnection failed', error);
      }
    }, delay);
  }

  private flushQueue(): void {
    const messages = this.messageQueue.flush();
    messages.forEach(msg => this.send(msg));
    if (messages.length > 0) {
      this.logger.info(`Flushed ${messages.length} queued messages`);
    }
  }
}
